import React from 'react';
import { Link } from 'react-router-dom';
import { Skeleton } from 'antd';
import { RightOutlined } from '@ant-design/icons';

/*
 * Shared admin-panel building blocks (styles in src/styles/ui.css). They mirror the mobile
 * admin components (Estethica-mobile/components/admin/AdminUI.tsx).
 */

export type Tone = 'primary' | 'success' | 'warning' | 'danger' | 'violet' | 'neutral';

const TONE_COLORS: Record<Tone, { color: string; soft: string }> = {
  primary: { color: 'var(--e22-primary)', soft: 'var(--e22-primary-soft)' },
  success: { color: 'var(--e22-success)', soft: 'var(--e22-success-soft)' },
  warning: { color: 'var(--e22-warning)', soft: 'var(--e22-warning-soft)' },
  danger: { color: 'var(--e22-danger)', soft: 'var(--e22-danger-soft)' },
  violet: { color: 'var(--e22-violet)', soft: 'var(--e22-violet-soft)' },
  neutral: { color: 'var(--e22-text-secondary)', soft: 'var(--e22-surface-muted)' },
};

export const PageHeader: React.FC<{
  title: string;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
}> = ({ title, subtitle, actions }) => (
  <div className="e22-page-header">
    <div>
      <h1 className="e22-page-title">{title}</h1>
      {subtitle ? <div className="e22-page-subtitle">{subtitle}</div> : null}
    </div>
    {actions ? <div className="e22-section-action">{actions}</div> : null}
  </div>
);

export const SectionCard: React.FC<{
  title: string;
  icon?: React.ReactNode;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}> = ({ title, icon, subtitle, action, children, className }) => (
  <section className={`e22-card e22-section ${className ?? ''}`}>
    <div className="e22-section-head">
      {icon ? <div className="e22-section-icon" aria-hidden>{icon}</div> : null}
      <div className="e22-section-heading">
        <h2 className="e22-section-title">{title}</h2>
        {subtitle ? <div className="e22-section-subtitle">{subtitle}</div> : null}
      </div>
      {action ? <div className="e22-section-action">{action}</div> : null}
    </div>
    {children}
  </section>
);

export const StatTile: React.FC<{
  label: string;
  value: number | string;
  icon: React.ReactNode;
  tone?: Tone;
  /** Makes the whole tile a link. */
  to?: string;
  loading?: boolean;
  footer?: React.ReactNode;
  alert?: boolean;
}> = ({ label, value, icon, tone = 'primary', to, loading, footer, alert }) => {
  const colors = TONE_COLORS[tone];
  const body = (
    <>
      <div className="e22-stat-top">
        <div className="e22-stat-icon" style={{ background: colors.soft, color: colors.color }} aria-hidden>
          {icon}
        </div>
        {to ? <RightOutlined className="e22-stat-arrow" aria-hidden /> : null}
      </div>
      <div>
        {loading ? (
          <Skeleton.Button active size="small" style={{ width: 56, height: 30 }} />
        ) : (
          <div className="e22-stat-value">{value}</div>
        )}
        <div className="e22-stat-label">{label}</div>
      </div>
      {footer ? <div className="e22-stat-footer" style={{ color: colors.color }}>{footer}</div> : null}
    </>
  );
  const className = `e22-card e22-stat ${alert ? 'e22-stat--alert' : ''}`;
  return to ? (
    <Link to={to} className={className}>
      {body}
    </Link>
  ) : (
    <div className={className}>{body}</div>
  );
};

export const Pill: React.FC<{ tone?: Tone; children: React.ReactNode; live?: boolean }> = ({
  tone = 'neutral',
  children,
  live,
}) => (
  <span className={`e22-pill ${tone !== 'neutral' ? `e22-pill--${tone}` : ''}`}>
    {live ? <span className="e22-live-dot" aria-hidden /> : null}
    {children}
  </span>
);

const AVATAR_COLORS = [
  { color: '#0A7EA4', soft: '#E0F2F8' },
  { color: '#7C3AED', soft: '#EDE9FE' },
  { color: '#DB2777', soft: '#FCE7F3' },
  { color: '#EA580C', soft: '#FFEDD5' },
  { color: '#16A34A', soft: '#DCFCE7' },
  { color: '#2563EB', soft: '#DBEAFE' },
];

/** Photo when there is one, otherwise coloured initials (colour is stable per name). */
export const PersonAvatar: React.FC<{
  name?: string;
  surname?: string;
  imageUrl?: string | null;
  size?: number;
  muted?: boolean;
}> = ({ name, surname, imageUrl, size = 36, muted }) => {
  const initials = `${name?.[0] ?? ''}${surname?.[0] ?? ''}`.toUpperCase() || '?';
  const index =
    `${name ?? ''}${surname ?? ''}`.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0) %
    AVATAR_COLORS.length;
  const colors = muted
    ? { color: 'var(--e22-text-muted)', soft: 'var(--e22-surface-muted)' }
    : AVATAR_COLORS[index];
  return (
    <div
      className="e22-avatar"
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.36),
        background: colors.soft,
        color: colors.color,
      }}
      aria-hidden
    >
      {imageUrl ? <img src={imageUrl} alt="" loading="lazy" /> : initials}
    </div>
  );
};

export const EmptyState: React.FC<{ icon: React.ReactNode; text: string; action?: React.ReactNode }> = ({
  icon,
  text,
  action,
}) => (
  <div className="e22-empty">
    <div className="e22-empty-icon" aria-hidden>{icon}</div>
    <div>{text}</div>
    {action}
  </div>
);

export interface FilterOption<K extends string | number> {
  key: K;
  label: string;
  count?: number;
}

/** A row of pill-shaped choices that wraps; use for 2–8 options. */
export function FilterChips<K extends string | number>({
  options,
  value,
  onChange,
  ariaLabel,
}: {
  options: FilterOption<K>[];
  value: K;
  onChange: (key: K) => void;
  ariaLabel: string;
}) {
  return (
    <div className="e22-filters" role="radiogroup" aria-label={ariaLabel}>
      {options.map((option) => {
        const active = option.key === value;
        return (
          <button
            key={option.key}
            type="button"
            role="radio"
            aria-checked={active}
            className={`e22-filter ${active ? 'e22-filter--active' : ''}`}
            onClick={() => onChange(option.key)}
          >
            {option.label}
            {option.count !== undefined ? <span className="e22-filter-count">{option.count}</span> : null}
          </button>
        );
      })}
    </div>
  );
}
